import React, { useEffect } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import _includes from 'lodash/includes';
import { setSelectedRestrictions } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { type IdentifyBodyLocationEnum, JmrFlowTypes } from 'src/utils/jmrUtils';
import {
  type AllRestrictionTypes,
  getEffectiveShiftLength,
  initWeightRestrictionTaskGroupChildren,
  isRestrictionTaskGroup,
  isTimeRestrictionTask,
  isWeightRestrictionTaskGroup,
  isYesNoRestrictionTask,
  type RestrictionOption,
  setupPreselectedRestrictionOptions,
  type TimeRestrictionTask,
  validateWeightRestrictionTaskGroupChildren,
  type WeightRestrictionTaskGroup,
  type YesNoRestrictionTask,
} from 'src/utils/restrictionUtils';

import RestrictionItemBox from '../restrictionItemBox';

import HoursSelector from './hoursSelector';
import IdentifyBodyLocationField from './identifyBodyLocationField';
import WeightRestrictionTaskGroupElements from './weightRestrictionTaskGroup';

// **** Constants **** //

// Exporting for testing
export const TOAST_MESSAGE = 'Please see the errors below, and try clicking "Next" again';

/**
 * Common props for every restriction selector. `restrictionItem` is generic so each selector
 * receives the concrete restriction type it renders (e.g. YesNoRestrictionTask).
 */
interface SelectorProps<T extends RestrictionOption = RestrictionOption> {
  restrictionItem: T;
  isDisabled: boolean;
  index: number;
  shiftLength: number;
  updateRestrictionOption: (arg: AllRestrictionTypes) => void;
}

// **** Functional Components **** //

/**
 * Main component.
 */
interface PhysicalRestrictionsProps {
  restrictionOptionsList: RestrictionOption[];
}
const PhysicalRestrictions = ({ restrictionOptionsList }: PhysicalRestrictionsProps) => {
  const dispatch = useAppDispatch();

  const { featureFlags } = useAppSelector((state) => state.user);
  const { selectedRestrictions, jmrFlowType, selectedTasks, backgroundFormData } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const shiftLength = getEffectiveShiftLength(backgroundFormData);
  const isDisabled = jmrFlowType === JmrFlowTypes.UpdateDates;

  // ------------------------------- onLoad -------------------------------- //

  // If updating a Placement, set the list of pre-selected items when the group of
  // selected jmr-tasks change
  useEffect(() => {
    const { CompleteTransfer, UpdateDates, AlternativePlacementRecommended } = JmrFlowTypes;
    const shouldReuseExistingRestrictions = _includes(
      [CompleteTransfer, UpdateDates, AlternativePlacementRecommended],
      jmrFlowType,
    );
    if (selectedTasks.length > 0 && shouldReuseExistingRestrictions && !selectedRestrictions.length) {
      const items = setupPreselectedRestrictionOptions(selectedTasks, restrictionOptionsList, shiftLength);
      if (items.length) {
        dispatch(setSelectedRestrictions(items));
      }
    }
  }, [selectedTasks, restrictionOptionsList, shiftLength, selectedRestrictions.length]);

  const updateRestrictionOption = (updatedRestriction: AllRestrictionTypes) =>
    dispatch(
      setSelectedRestrictions(
        selectedRestrictions.map((original) =>
          original.entityId === updatedRestriction.entityId ? updatedRestriction : original,
        ),
      ),
    );

  const onRemoveRestriction = (restrictionItem: RestrictionOption) => {
    dispatch(
      setSelectedRestrictions(selectedRestrictions.filter(({ entityId }) => entityId !== restrictionItem.entityId)),
    );
  };

  return (
    <Column spacing="400" spacingInset="400">
      {selectedRestrictions.length && featureFlags.NON_PHYSICAL_LIMITATIONS ? (
        <Heading className="newJmr__restrictionPage__heading" alignment="center" level={4}>
          Physical Restrictions
        </Heading>
      ) : null}
      {selectedRestrictions.map((item, index) => {
        const selectorProps = { isDisabled, index, shiftLength, updateRestrictionOption };
        return (
          <RestrictionItemBox
            restrictionNumber={index}
            key={item.entityId}
            label={item.label}
            onRemoveRestriction={() => onRemoveRestriction(item)}
          >
            {item.identifyBodyLocation || (isDisabled && !!item.bodyLocationValue) ? (
              <IdentifyBodyLocationSelector restrictionItem={item} {...selectorProps} />
            ) : null}
            {isTimeRestrictionTask(item) ? (
              <TimeRestrictionTaskSelector restrictionItem={item} {...selectorProps} />
            ) : null}
            {isYesNoRestrictionTask(item) ? (
              <RestrictionNotAllowText restrictionItem={item} {...selectorProps} />
            ) : null}
            {isWeightRestrictionTaskGroup(item) ? (
              <WeightRestrictionGroupMaxRangeSelector restrictionItem={item} {...selectorProps} />
            ) : null}
            {isWeightRestrictionTaskGroup(item) && item.maxWeightRange !== undefined && item.maxWeightRange > 0 ? (
              <WeightRestrictionGroupSliderSelector restrictionItem={item} {...selectorProps} />
            ) : null}
            {isWeightRestrictionTaskGroup(item) && item.showChildSelectors ? (
              <WeightRestrictionGroupSelector restrictionItem={item} {...selectorProps} />
            ) : null}
            {isRestrictionTaskGroup(item) && !isWeightRestrictionTaskGroup(item) ? (
              <NonWeightMeasuredGroupText />
            ) : null}
          </RestrictionItemBox>
        );
      })}
    </Column>
  );
};

// Render body-location selector (applies to both tasks and groups)
const IdentifyBodyLocationSelector = ({ restrictionItem, isDisabled, updateRestrictionOption }: SelectorProps) => {
  const { identifyBodyLocation, bodyLocationValue } = restrictionItem;
  if (!(identifyBodyLocation || (isDisabled && !!bodyLocationValue))) {
    return null;
  }
  const onChange = (value: IdentifyBodyLocationEnum | null) => {
    const toUpdate = { ...restrictionItem, bodyLocationValue: value };
    if (isRestrictionTaskGroup(toUpdate)) {
      toUpdate.restrictionTasks = toUpdate.restrictionTasks.map((child) => ({
        ...child,
        bodyLocationValue: value,
      }));
    }
    updateRestrictionOption(toUpdate);
  };
  return (
    <IdentifyBodyLocationField
      spacingInset="none none 400 none"
      bodyLocationValue={restrictionItem.bodyLocationValue ?? null}
      onChangeBodyLocationValue={onChange}
      isDisabled={isDisabled}
    />
  );
};

// Render time-restriction task
const TimeRestrictionTaskSelector = ({
  restrictionItem,
  isDisabled,
  index,
  shiftLength,
  updateRestrictionOption,
}: SelectorProps<TimeRestrictionTask>) => {
  const onChange = (newValue: string) => {
    const newValueStr = String(newValue);
    if (newValueStr !== restrictionItem.restrictedValue) {
      const { error, ...toUpdate } = restrictionItem;
      toUpdate.restrictedValue = newValueStr;
      updateRestrictionOption(toUpdate);
    }
  };
  return (
    <HoursSelector
      selectedValue={restrictionItem.restrictedValue}
      onSelectValue={onChange}
      isDisabled={isDisabled}
      shiftLength={shiftLength}
      restrictionLabel={restrictionItem.label}
      errorMessage={restrictionItem.error}
      index={index}
    />
  );
};

// Yes/no tasks are always set to false
const RestrictionNotAllowText = ({ restrictionItem }: SelectorProps<YesNoRestrictionTask>) => (
  <Box spacingInset="200">
    <Text color="secondary" data-testid="new-jmr__yes-no-restriction-field-label">
      0 hrs (Associate cannot perform <b>{restrictionItem?.label}</b>)
    </Text>
  </Box>
);

// Handle <MaxRangeSelector /> updates
const WeightRestrictionGroupMaxRangeSelector = ({
  restrictionItem,
  isDisabled,
  index,
  shiftLength,
  updateRestrictionOption,
}: SelectorProps<WeightRestrictionTaskGroup>) => {
  const onChange = (value: number) => {
    const { error, ...toUpdate } = restrictionItem;
    toUpdate.maxWeightRange = value;
    if (restrictionItem.showChildSelectors && value === 0) {
      toUpdate.showChildSelectors = false;
    }
    toUpdate.restrictionTasks = initWeightRestrictionTaskGroupChildren(toUpdate, String(shiftLength));
    updateRestrictionOption(toUpdate);
  };
  return (
    <WeightRestrictionTaskGroupElements.MaxRangeSelector
      restrictionLabel={restrictionItem.label}
      restrictionMaxUnit={restrictionItem.restrictionMaxUnit}
      restrictionChildTasks={restrictionItem.restrictionTasks}
      selectedValue={restrictionItem.maxWeightRange}
      onSelectValue={onChange}
      isDisabled={isDisabled}
      index={index}
      spacingInset="none none 400 none"
      errorMessage={restrictionItem.error}
    />
  );
};

// Show the toggle (slider element) for displaying child weight selectors
const WeightRestrictionGroupSliderSelector = ({
  restrictionItem,
  isDisabled,
  index,
  shiftLength,
  updateRestrictionOption,
}: SelectorProps<WeightRestrictionTaskGroup>) => {
  if (!(restrictionItem.maxWeightRange !== undefined && restrictionItem.maxWeightRange > 0)) {
    return null;
  }
  const onToggleValue = (value: boolean) => {
    const toUpdate = { ...restrictionItem, showChildSelectors: value };
    if (!value) {
      toUpdate.restrictionTasks = initWeightRestrictionTaskGroupChildren(toUpdate, String(shiftLength));
    }
    updateRestrictionOption(toUpdate);
  };
  return (
    <WeightRestrictionTaskGroupElements.ToggleShowChildSelectors
      restrictionLabel={restrictionItem.label}
      restrictionMaxUnit={restrictionItem.restrictionMaxUnit}
      maxWeightRange={restrictionItem.maxWeightRange}
      value={!!restrictionItem.showChildSelectors}
      onToggleValue={onToggleValue}
      isDisabled={isDisabled}
      index={index}
    />
  );
};

// Update parent when child weight selector is updated
const WeightRestrictionGroupSelector = ({
  restrictionItem,
  isDisabled,
  index,
  shiftLength,
  updateRestrictionOption,
}: SelectorProps<WeightRestrictionTaskGroup>) => {
  if (!restrictionItem.showChildSelectors) {
    return null;
  }

  const onUpdateChildSelection = (childIndex: number, value: number) => {
    const children = [...restrictionItem.restrictionTasks];
    children[childIndex] = { ...children[childIndex], restrictedValue: String(value) };

    updateRestrictionOption({
      ...restrictionItem,
      restrictionTasks: validateWeightRestrictionTaskGroupChildren(children),
    });
  };

  return (
    <WeightRestrictionTaskGroupElements.ChildSelectors
      index={index}
      childSelection={restrictionItem.restrictionTasks}
      shiftLength={shiftLength}
      isDisabled={isDisabled}
      onUpdateChildSelection={onUpdateChildSelection}
    />
  );
};

// So far, only weight-measured groups are supported
const NonWeightMeasuredGroupText = () => (
  <Text color="secondary">
    <i>
      Only weight measured task-groups can be configured at this time. This task will not be used for Job Match Results.
    </i>
  </Text>
);

// **** Export **** //

export default PhysicalRestrictions;
